import { Button } from "@interstate/components/Button";
import { ArrowTopRightOnSquareIcon } from "@interstate/components/Icons";
import { useToaster } from "@interstate/components/Toast";
import { useContext, useMemo, useState } from "react";
import { Spinner } from "@interstate/components/Spinner";
import {
  ConversationContext,
  ConversationDetailsContext,
  useTextReply
} from "../../context";
import {
  ConversationDirection,
  ConversationTypeEnum,
  ConversationViewTypeEnum,
  EventResultEnum,
  PendingEndlink
} from "@coxauto-ui/cxm/communications/interfaces";
import { replyButtonTestId } from "../../testIds";
import {
  SendCommunicationRequest,
  getConversationItemFromSendAction,
  isResponseError,
  sendCommunication
} from "@coxauto-ui/cxm/communications/api";
import { TagManager } from "@coxauto-ui/core-third-party-ga4";
import {
  ConditionalToolTipWrapper,
  notifyCommunicationSent
} from "@coxauto-ui/cxm/communications/util";
import { ButtonContainer, removeVideoUrls } from "./message-send-button-shared";
import { FileUploadErrorAlert } from "../reply-alerts";

export interface TextMessageSendButtonProps {
  onMessageSendFailed: () => void;
  onMessageSendFailedSetMsg: (msg: string) => void;
  isTextAndNoPhoneNumber: boolean;
  isTextWithRepairOrderOnly: boolean;
}

type SendIteration =
  | { kind: "body" }
  | { kind: "endlink"; endlink: PendingEndlink };

export function TextMessageSendButton(props: TextMessageSendButtonProps) {
  const [sendTextResponseMsg, setTextResponseMsg] = useState("");
  const { toast: toaster } = useToaster();
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const noop = () => {};

  const {
    autoLeadId,
    customerId,
    customerSystem,
    defaultTab,
    env,
    getUserName,
    hasActiveTask,
    setDefaultMessageContent,
    taskId,
    serviceAppointmentId,
    completeTask,
    inboxMessageHandler,
    isIframeMode
  } = useContext(ConversationContext);

  const {
    setConversationItems,
    selectedConversationItem,
    selectedReplyOption,
    toggleSetShowReply
  } = useContext(ConversationDetailsContext);

  const {
    replyText,
    setReplyText,
    attachments: messageAttachments,
    setAttachments,
    selectedDealerPhoneInformation,
    setSelectedDealerPhoneInformation,
    selectedToChannelValue,
    setSelectedToChannelValue,
    isTextOptInPending,
    isPhoneValidationBlocking,
    setSelectedMedia,
    pendingEndlinks,
    setPendingEndlinks
  } = useTextReply();

  const [loading, setLoading] = useState(false);

  // Explicit close, not toggle: a tab-change effect can flip showReply during
  // the loop's optimistic inserts; a toggle here would reopen the pane.
  const closeOnSend = () => toggleSetShowReply && toggleSetShowReply(false);

  const clearTextReplyFieldsAndDefaults = () => {
    setReplyText("");
    setAttachments([]);
    setSelectedToChannelValue("");
    setSelectedDealerPhoneInformation(prev => ({ ...prev, phoneNumber: "" }));
    setSelectedMedia([]);
    setPendingEndlinks([]);
    setDefaultMessageContent(null);
  };

  const applyDisabled = useMemo(() => {
    const hasContent =
      replyText?.trim().length > 0 || pendingEndlinks.length > 0;
    return (
      !selectedReplyOption ||
      loading ||
      !hasContent ||
      isTextOptInPending ||
      props.isTextAndNoPhoneNumber ||
      props.isTextWithRepairOrderOnly ||
      isPhoneValidationBlocking
    );
  }, [
    selectedReplyOption,
    loading,
    replyText,
    pendingEndlinks,
    isTextOptInPending,
    props.isTextAndNoPhoneNumber,
    props.isTextWithRepairOrderOnly,
    isPhoneValidationBlocking
  ]);

  const sendReply = async () => {
    setTextResponseMsg("");

    const hasText = replyText.trim().length > 0;
    const hasEndlinks = pendingEndlinks.length > 0;

    if ((!hasText && !hasEndlinks) || !selectedReplyOption) {
      return;
    }

    setLoading(true);

    if (selectedReplyOption?.replyInline) {
      const channelValue = selectedToChannelValue || "";

      const iterations: SendIteration[] = [
        ...(hasText ? [{ kind: "body" as const }] : []),
        ...pendingEndlinks.map(endlink => ({
          kind: "endlink" as const,
          endlink
        }))
      ];

      // baseTime + i gives each temp item a deterministic, monotonic key/date —
      // a defensive backstop for the FIFO orphan-cleanup path in
      // mergeConversationItems (primary reconciliation is content-based).
      const baseTime = Date.now();
      const textAttachmentKeys = messageAttachments
        .filter(p => !p.contentType.includes("video/"))
        .map(p => p.key);
      const replyContext =
        selectedConversationItem != null
          ? ConversationViewTypeEnum.DETAIL
          : ConversationViewTypeEnum.LIST;

      const markTempFailed = (tempKey: number) =>
        setConversationItems(prev =>
          prev.map(item =>
            item.key === tempKey
              ? { ...item, eventResult: EventResultEnum.FAILED }
              : item
          )
        );

      let anyFailed = false;
      let anySucceeded = false;

      for (let i = 0; i < iterations.length; i++) {
        const iteration = iterations[i];
        const isBody = iteration.kind === "body";
        const content = isBody ? replyText : iteration.endlink.endLink;

        const sendCommsRequest: SendCommunicationRequest = {
          autoLeadId,
          consumerId: customerId,
          consumerContactInfo: channelValue,
          consumerRecordSystem: customerSystem,
          taskId: hasActiveTask ? taskId : undefined,
          content,
          type: ConversationTypeEnum.TEXT,
          subject: "",
          // Endlink iters must be single-URL only for rich previews to render.
          attachments: isBody ? textAttachmentKeys : [],
          serviceAppointmentId
        };

        if (!isBody) {
          sendCommsRequest.mediaInfo = {
            mediaIds: [iteration.endlink.mediaId],
            mediaViewTrackingIds: [iteration.endlink.mediaViewTrackingId],
            mediaItems: [
              {
                title: iteration.endlink.title,
                endLink: iteration.endlink.endLink
              }
            ]
          };
        }

        if (selectedDealerPhoneInformation.phoneNumber) {
          sendCommsRequest.fromPhoneNumber =
            selectedDealerPhoneInformation.phoneNumber;
        }

        const tempItemBase = getConversationItemFromSendAction(
          getUserName(),
          ConversationDirection.Outbound,
          ConversationTypeEnum.TEXT,
          channelValue,
          new Date(baseTime + i),
          isBody ? removeVideoUrls(content.substring(0, 300)) : content,
          "",
          undefined,
          isBody ? messageAttachments : []
        );
        const tempKey = -(baseTime + i);
        const tempItem = {
          ...tempItemBase,
          key: tempKey,
          date: new Date(baseTime + i).toISOString()
        };
        setConversationItems(current => [...current, tempItem]);

        let breakLoop = false;
        let iterationFailure: unknown = null;

        try {
          const response = await sendCommunication(sendCommsRequest, env);

          if (response === true) {
            anySucceeded = true;
            TagManager.event({
              event: "productEvent",
              eventName: "Message Sent",
              eventProperties: {
                packagedBusinessCapability: "OCC Inbox",
                commonCustomerId: customerId,
                communicationChannel: ConversationTypeEnum.TEXT,
                replyContext,
                errorMessage: undefined
              }
            });

            if (isIframeMode && inboxMessageHandler) {
              notifyCommunicationSent(
                inboxMessageHandler,
                "TEXT",
                ConversationTypeEnum.TEXT,
                true,
                hasActiveTask,
                undefined,
                isBody && messageAttachments.length > 0
                  ? messageAttachments.length
                  : undefined
              );
            }
            continue;
          }

          iterationFailure = response;
        } catch (err) {
          iterationFailure = err;
        }

        // 400 attachment-cap and 403 compliance break the loop — body and
        // endlinks are one package, so channel-level validation halts the
        // remaining iterations. Anything else continues.
        anyFailed = true;
        markTempFailed(tempKey);

        let error = "Failed to send message.";
        const typedError =
          iterationFailure &&
          typeof iterationFailure === "object" &&
          isResponseError(iterationFailure)
            ? iterationFailure
            : null;
        if (typedError) {
          if (typedError.statusCode === 400) {
            error = typedError.errorResult?.message as string;
            if (
              error?.includes(
                "The message can only have a maximum of 10 attachments"
              )
            ) {
              error = "The message can only have a maximum of 10 attachments";
              setTextResponseMsg(error);
              breakLoop = true;
            }
          }
          if (typedError.statusCode === 403) {
            props.onMessageSendFailedSetMsg("Compliance Issue");
            props.onMessageSendFailed();
            breakLoop = true;
          }
        }

        TagManager.event({
          event: "productEvent",
          eventName: "Error Displayed",
          eventProperties: {
            packagedBusinessCapability: "OCC Inbox",
            commonCustomerId: customerId,
            communicationChannel: ConversationTypeEnum.TEXT,
            replyContext: undefined,
            errorMessage: error
          }
        });

        if (isIframeMode && inboxMessageHandler) {
          notifyCommunicationSent(
            inboxMessageHandler,
            "TEXT",
            ConversationTypeEnum.TEXT,
            false,
            hasActiveTask
          );
        }

        if (breakLoop) break;
      }

      if (anyFailed) {
        toaster(
          "Error",
          "One or more messages failed to send.",
          noop,
          undefined
        );
      }

      clearTextReplyFieldsAndDefaults();

      if (
        anySucceeded &&
        hasActiveTask &&
        defaultTab === ConversationTypeEnum.TEXT
      ) {
        completeTask();
      }

      closeOnSend();
    } else {
      window.open(selectedReplyOption?.replyUrl);
    }
    setLoading(false);
  };

  return (
    <ButtonContainer>
      <FileUploadErrorAlert message={sendTextResponseMsg} />
      <ConditionalToolTipWrapper
        condition={props.isTextAndNoPhoneNumber}
        toolTipContent="Text messaging is unavailable because no dealer phone number is configured."
      >
        <Button
          data-testid={replyButtonTestId}
          disabled={applyDisabled}
          id="conversation-reply-button"
          onClick={() => sendReply()}
        >
          {!selectedReplyOption?.replyInline ? (
            <>
              <ArrowTopRightOnSquareIcon /> Reply In External Application
            </>
          ) : (
            "Send Text"
          )}
          {loading && (
            <Spinner
              id="conversation-reply-button-loading-indicator"
              size={30}
            />
          )}
        </Button>
      </ConditionalToolTipWrapper>
    </ButtonContainer>
  );
}

export default TextMessageSendButton;
